import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AccountsTable = ({ accounts, onQuickAction }) => {
  const [sortField, setSortField] = useState('companyName');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAccounts = [...accounts]?.sort((a, b) => {
    let aVal = a?.[sortField];
    let bVal = b?.[sortField];

    if (typeof aVal === 'string') {
      aVal = aVal?.toLowerCase();
      bVal = bVal?.toLowerCase();
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getComplianceStatus = (score) => {
    if (score >= 90) return { label: 'Excellent', color: 'text-success', bg: 'bg-success/10' };
    if (score >= 70) return { label: 'Good', color: 'text-warning', bg: 'bg-warning/10' };
    if (score >= 50) return { label: 'Warning', color: 'text-warning', bg: 'bg-warning/10' };
    return { label: 'Critical', color: 'text-error', bg: 'bg-error/10' };
  };

  const getBillingStatus = (nextBillingDate) => {
    if (!nextBillingDate) return { label: 'Overdue', color: 'text-error', bg: 'bg-error/10' };
    
    const daysUntilBilling = Math.floor(
      (new Date(nextBillingDate) - new Date()) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilBilling < 0) return { label: 'Overdue', color: 'text-error', bg: 'bg-error/10' };
    if (daysUntilBilling <= 7) return { label: 'Due Soon', color: 'text-warning', bg: 'bg-warning/10' };
    return { label: 'Current', color: 'text-success', bg: 'bg-success/10' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const SortableHeader = ({ field, label }) => (
    <th
      className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-2">
        {label}
        {sortField === field && (
          <Icon
            name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
            size={14}
          />
        )}
      </div>
    </th>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            <SortableHeader field="companyName" label="Company Name" />
            <SortableHeader field="employeeCount" label="Employees" />
            <SortableHeader field="subscriptionTier" label="Tier" />
            <SortableHeader field="complianceScore" label="Compliance" />
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Billing Status</th>
            <SortableHeader field="lastActivityDate" label="Last Activity" />
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedAccounts?.length === 0 ? (
            <tr>
              <td colSpan="7" className="text-center py-8 text-muted-foreground">
                No accounts found
              </td>
            </tr>
          ) : (
            sortedAccounts?.map((account) => {
              const complianceStatus = getComplianceStatus(account?.complianceScore || 0);
              const billingStatus = getBillingStatus(account?.nextBillingDate);

              return (
                <tr key={account?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">{account?.companyName}</p>
                      <p className="text-xs text-muted-foreground">{account?.contactEmail}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    {account?.employeeCount}
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {account?.subscriptionTier}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${complianceStatus?.color}`}>
                        {account?.complianceScore || 0}%
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${complianceStatus?.bg} ${complianceStatus?.color}`}>
                        {complianceStatus?.label}
                      </span>
                    </div>
                    {account?.criticalViolations > 0 && (
                      <p className="text-xs text-error mt-1">
                        {account?.criticalViolations} critical violations
                      </p>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${billingStatus?.bg} ${billingStatus?.color}`}>
                        {billingStatus?.label}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">
                        Next: {formatDate(account?.nextBillingDate)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        K{account?.monthlyPriceKina?.toLocaleString()}/mo
                      </p>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    {formatDate(account?.lastActivityDate)}
                  </td>
                  <td className="py-3 px-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onQuickAction?.(account)}
                    >
                      <Icon name="Settings" size={14} className="mr-1" />
                      Manage
                    </Button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AccountsTable;